/**
 * Client-facing Usage & Credits report exporters — PDF, Markdown, Excel, PPTX.
 *
 * All four run entirely in the browser from the data the page already has, so a
 * client can hand the report to a stakeholder without us generating anything
 * server-side. The heavy libraries (jspdf, xlsx, pptxgenjs) are pulled in with
 * dynamic import() so they land in their own chunks and never weigh down the
 * main bundle — a client who never clicks Export never downloads them.
 *
 * Margin safety: like the page itself, every export carries CREDITS only —
 * never a raw provider dollar cost.
 */
import type { UsageCredits } from './usageCreditsService';
import type { UsagePiece } from './usagePiecesService';

function fmtDate(iso: string): string {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function niceName(tenantId: string): string {
  // "aaafence.com" -> "Aaafence" for a human-facing title.
  const base = tenantId.replace(/\.[a-z]+$/i, '').replace(/[-_.]/g, ' ');
  return base.replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettyModel(model: string | null): string {
  if (!model) return '—';
  return model.includes('/') ? model.slice(model.lastIndexOf('/') + 1) : model;
}

interface PieceRow {
  title: string;
  template: string;
  words: number | null;
  attempts: number;
  model: string;
  quality: number | null;
  credits: number;
  published: string;
  url: string | null;
}

export interface ReportModel {
  tenantId: string;
  clientName: string;
  periodLabel: string;
  creditsUsed: number;
  completed: number;
  failed: number;
  totalPieces: number;
  avgPerPiece: number;
  creditDollarValue: number;
  daily: { date: string; credits: number }[];
  pieces: PieceRow[];
  fileStem: string;
}

function usd(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "125 cr ($12.50)" — credits primary, dollar at the published rate alongside. */
function creditCost(credits: number, rate: number): string {
  return `${credits.toLocaleString()} cr (${usd(credits * rate)})`;
}

export function buildReportModel(data: UsageCredits, pieces: UsagePiece[] = []): ReportModel {
  const totalPieces = data.completed_count + data.failed_count;
  const avgPerPiece =
    data.completed_count > 0 ? Math.round(data.credits_used / data.completed_count) : 0;
  return {
    tenantId: data.tenant_id,
    clientName: niceName(data.tenant_id),
    periodLabel: `${fmtDate(data.period_start)} – ${fmtDate(data.period_end)}`,
    creditsUsed: data.credits_used,
    completed: data.completed_count,
    failed: data.failed_count,
    totalPieces,
    avgPerPiece,
    creditDollarValue: data.credit_dollar_value,
    daily: data.daily.map((d) => ({ date: fmtDate(d.date), credits: d.credits })),
    pieces: pieces.map((p) => ({
      title: p.title,
      template: p.template ?? '—',
      words: p.word_count,
      attempts: p.attempts,
      model: prettyModel(p.model),
      quality: p.quality_score,
      credits: p.credits,
      published: p.published ? 'Yes' : 'No',
      url: p.wordpress_url,
    })),
    fileStem: `${data.tenant_id}-usage-credits-${data.period_start}`,
  };
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has committed.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const SUMMARY_ROWS = (m: ReportModel): [string, string][] => [
  ['Credits used this cycle', m.creditsUsed.toLocaleString()],
  ['Completed pieces', m.completed.toLocaleString()],
  ['Not completed', m.failed.toLocaleString()],
  ['Total generation runs', m.totalPieces.toLocaleString()],
  ['Avg credits / completed piece', m.avgPerPiece.toLocaleString()],
];

const PIECE_HEADERS = ['Title', 'Template', 'Words', 'Attempts', 'Model', 'Quality', 'Cost', 'Published'];

function num(v: number | null): string {
  return v !== null && v !== undefined ? v.toLocaleString() : '—';
}

/* ------------------------------- PDF -------------------------------------- */
export async function exportPdf(data: UsageCredits, pieces: UsagePiece[]): Promise<void> {
  const m = buildReportModel(data, pieces);
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const marginX = 40;
  const coral: [number, number, number] = [217, 119, 87];

  doc.setFontSize(18);
  doc.text('Usage & Credits', marginX, 54);
  doc.setFontSize(11);
  doc.setTextColor(120);
  doc.text(`${m.clientName} · ${m.periodLabel}`, marginX, 72);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 92,
    head: [['Summary', '']],
    body: SUMMARY_ROWS(m),
    theme: 'plain',
    headStyles: { fontStyle: 'bold', textColor: coral },
    columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
  });

  autoTable(doc, {
    // @ts-expect-error autotable augments doc with lastAutoTable at runtime
    startY: (doc.lastAutoTable?.finalY ?? 200) + 24,
    head: [PIECE_HEADERS],
    body: m.pieces.map((p) => [
      p.title,
      p.template,
      num(p.words),
      String(p.attempts),
      p.model,
      num(p.quality),
      creditCost(p.credits, m.creditDollarValue),
      p.published,
    ]),
    headStyles: { fillColor: coral },
    columnStyles: {
      2: { halign: 'right' },
      3: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
    },
    styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
  });

  doc.save(`${m.fileStem}.pdf`);
}

/* ----------------------------- Markdown ----------------------------------- */
export async function exportMarkdown(data: UsageCredits, pieces: UsagePiece[]): Promise<void> {
  const m = buildReportModel(data, pieces);
  const esc = (s: string) => s.replace(/\|/g, '\\|');
  const lines: string[] = [
    `# Usage & Credits — ${m.clientName}`,
    '',
    `**Cycle:** ${m.periodLabel}`,
    '',
    '## Summary',
    '',
    '| Metric | Value |',
    '| --- | ---: |',
    ...SUMMARY_ROWS(m).map(([k, v]) => `| ${k} | ${v} |`),
    '',
    '## Pieces',
    '',
    '| Title | Template | Words | Attempts | Model | Quality | Cost | Published |',
    '| --- | --- | ---: | ---: | --- | ---: | ---: | --- |',
    ...m.pieces.map(
      (p) =>
        `| ${esc(p.title)} | ${esc(p.template)} | ${num(p.words)} | ${p.attempts} | ${esc(p.model)} | ${num(p.quality)} | ${creditCost(p.credits, m.creditDollarValue)} | ${p.published} |`,
    ),
    '',
    '## Credits per day',
    '',
    '| Date | Credits |',
    '| --- | ---: |',
    ...m.daily.map((d) => `| ${d.date} | ${d.credits.toLocaleString()} |`),
    '',
    `_Credits only; 1 credit ≈ $${data.credit_dollar_value.toFixed(2)} of underlying cost._`,
    '',
  ];
  downloadBlob(new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' }), `${m.fileStem}.md`);
}

/* ------------------------------- Excel ------------------------------------ */
export async function exportExcel(data: UsageCredits, pieces: UsagePiece[]): Promise<void> {
  const m = buildReportModel(data, pieces);
  const XLSX = await import('xlsx');

  const summary = XLSX.utils.aoa_to_sheet([
    ['Usage & Credits', m.clientName],
    ['Cycle', m.periodLabel],
    [],
    ['Metric', 'Value'],
    ...SUMMARY_ROWS(m).map(([k, v]) => [k, v]),
  ]);
  summary['!cols'] = [{ wch: 32 }, { wch: 20 }];

  const piecesSheet = XLSX.utils.json_to_sheet(
    m.pieces.map((p) => ({
      Title: p.title,
      Template: p.template,
      Words: p.words ?? '',
      Attempts: p.attempts,
      Model: p.model,
      Quality: p.quality ?? '',
      // Two numeric columns (not a combined string) so Excel can sort/sum them.
      'Cost (credits)': p.credits,
      'Cost (USD)': Number((p.credits * m.creditDollarValue).toFixed(2)),
      Published: p.published,
      'WordPress URL': p.url ?? '',
    })),
  );
  piecesSheet['!cols'] = [
    { wch: 44 }, { wch: 16 }, { wch: 8 }, { wch: 9 }, { wch: 22 },
    { wch: 8 }, { wch: 13 }, { wch: 11 }, { wch: 10 }, { wch: 40 },
  ];

  const daily = XLSX.utils.json_to_sheet(m.daily.map((d) => ({ Date: d.date, Credits: d.credits })));
  daily['!cols'] = [{ wch: 16 }, { wch: 10 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, summary, 'Summary');
  XLSX.utils.book_append_sheet(wb, piecesSheet, 'Pieces');
  XLSX.utils.book_append_sheet(wb, daily, 'Daily');
  XLSX.writeFile(wb, `${m.fileStem}.xlsx`);
}

/* ------------------------------- PPTX ------------------------------------- */
export async function exportPptx(data: UsageCredits, pieces: UsagePiece[]): Promise<void> {
  const m = buildReportModel(data, pieces);
  const PptxGenJS = (await import('pptxgenjs')).default;
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';

  const CORAL = 'D97757';
  const INK = '221E1A';
  const MUTED = '857971';

  // Title + headline metric.
  const title = pptx.addSlide();
  title.background = { color: 'FDFAF7' };
  title.addText('Usage & Credits', { x: 0.6, y: 0.6, fontSize: 32, bold: true, color: INK });
  title.addText(`${m.clientName}  ·  ${m.periodLabel}`, {
    x: 0.6, y: 1.4, fontSize: 16, color: MUTED,
  });
  title.addText(m.creditsUsed.toLocaleString(), {
    x: 0.6, y: 2.4, fontSize: 72, bold: true, color: CORAL,
  });
  title.addText('credits used this cycle', { x: 0.6, y: 4.0, fontSize: 16, color: MUTED });

  // Summary table.
  const s2 = pptx.addSlide();
  s2.background = { color: 'FFFFFF' };
  s2.addText('Summary', { x: 0.6, y: 0.5, fontSize: 24, bold: true, color: INK });
  s2.addTable(
    [
      [
        { text: 'Metric', options: { bold: true, color: 'FFFFFF', fill: { color: CORAL } } },
        { text: 'Value', options: { bold: true, color: 'FFFFFF', fill: { color: CORAL }, align: 'right' } },
      ],
      ...SUMMARY_ROWS(m).map(([k, v]) => [
        { text: k },
        { text: v, options: { align: 'right' as const } },
      ]),
    ],
    { x: 0.6, y: 1.2, w: 8, fontSize: 14, border: { type: 'solid', color: 'E8DFD7', pt: 1 }, color: INK },
  );

  // Pieces table (cap rows so the slide stays legible).
  const cols = ['Title', 'Template', 'Words', 'Model', 'Quality', 'Cost'];
  const s3 = pptx.addSlide();
  s3.background = { color: 'FFFFFF' };
  s3.addText('Pieces', { x: 0.6, y: 0.5, fontSize: 24, bold: true, color: INK });
  s3.addTable(
    [
      cols.map((h) => ({ text: h, options: { bold: true, color: 'FFFFFF', fill: { color: CORAL } } })),
      ...m.pieces.slice(0, 12).map((p) => [
        { text: p.title },
        { text: p.template },
        { text: num(p.words), options: { align: 'right' as const } },
        { text: p.model },
        { text: num(p.quality), options: { align: 'right' as const } },
        { text: creditCost(p.credits, m.creditDollarValue), options: { align: 'right' as const } },
      ]),
    ],
    { x: 0.6, y: 1.2, w: 12, fontSize: 12, border: { type: 'solid', color: 'E8DFD7', pt: 1 }, color: INK },
  );

  await pptx.writeFile({ fileName: `${m.fileStem}.pptx` });
}

export type ReportFormat = 'pdf' | 'md' | 'xlsx' | 'pptx';

export async function exportReport(
  format: ReportFormat,
  data: UsageCredits,
  pieces: UsagePiece[] = [],
): Promise<void> {
  switch (format) {
    case 'pdf':
      return exportPdf(data, pieces);
    case 'md':
      return exportMarkdown(data, pieces);
    case 'xlsx':
      return exportExcel(data, pieces);
    case 'pptx':
      return exportPptx(data, pieces);
  }
}
